import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { changePassword } from '../api/auth';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 成功后的回调（可选：刷新用户信息/提示）
};

const DoctorChangePasswordDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  if (!open) return null;

  const pwPolicyOk = /^(?!.*\s)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/
  const canSubmit = !!currentPwd && !!newPwd && newPwd === newPwd2 && pwPolicyOk && !loading;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setOk(false);
    setLoading(true);
    try {
      await changePassword(currentPwd, newPwd, newPwd2);
      setOk(true);
      setCurrentPwd('');
      setNewPwd('');
      setNewPwd2('');
      onSuccess?.();
    } catch (e: any) {
      const code = e?.detail || e?.code || e?.message;
      if (code === 'current_password_incorrect' || /当前密码不正确/.test(code)) setErr(t('password.current_incorrect'));
      else if (code === 'weak_password' || /policy/i.test(code)) setErr(t('password.weak_policy'));
      else if (code === 'password_reused' || /相同/.test(code)) setErr(t('password.reused'));
      else if (code === 'password_mismatch' || /不一致/.test(code)) setErr(t('password.mismatch'));
      else setErr(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold mb-4">{t('password.change')}</h2>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm mb-1">{t('password.current')}</label>
            <input
              type="password"
              className="w-full border rounded-lg px-3 py-2"
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm mb-1">{t('password.new')}</label>
            <input
              type="password"
              className="w-full border rounded-lg px-3 py-2"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{t('password.policy_hint')}</p>
          </div>

          <div>
            <label className="block text-sm mb-1">{t('password.new_confirm')}</label>
            <input
              type="password"
              className="w-full border rounded-lg px-3 py-2"
              value={newPwd2}
              onChange={(e) => setNewPwd2(e.target.value)}
              required
            />
            {newPwd2 && newPwd !== newPwd2 && (
              <p className="text-xs text-red-600 mt-1">{t('password.mismatch')}</p>
            )}
            {newPwd && !pwPolicyOk && (
              <p className="text-xs text-red-600 mt-1">{t('password.weak_policy')}</p>
            )}
          </div>

          {err && <p className="text-sm text-red-600">{err}</p>}
          {ok && <p className="text-sm text-green-600">{t('password.changed_ok')}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={classNames(
                'px-4 py-2 rounded-lg text-sm text-white',
                canSubmit ? 'bg-coral hover:opacity-90' : 'bg-gray-400 cursor-not-allowed'
              )}
            >
              {loading ? t('common.saving') : t('password.change')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorChangePasswordDialog;
